import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Box, Button, TablePagination } from '@mui/material';
import axios from 'axios';
import { Download } from '@mui/icons-material';

const planMap = {
  FREE_TRIAL: "Free Trial",
  free_trial: "Free Trial",
  PAY_AS_YOU_GO: "Pay As You Go",
  MONTHLY: "Starter",
  monthly: "Starter",           // Add this
  PROFESSIONAL: "Professional",
  professional: "Professional", // Add this
  OVERAGE_JOB: "Overage",
  overage_job: "Overage"        // Add this
};

function Payment() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    async function fetchPayments() {
      try {
        const API_BASE =
          process.env.REACT_APP_API_URL || "https://api.exihire.com";

        const endpoint = window.location.pathname.includes('super-admin')
          ? `${API_BASE}/api/payment/super-admin/payments`
          : `${API_BASE}/api/payment/history`;
        const response = await axios.get(endpoint, {
                withCredentials: true
              });
        if (Array.isArray(response.data)) {
          setPayments(response.data);
        } else {
          console.error('Expected array for payments, got:', response.data);
          setPayments([]);
        }
      } catch (error) {
        console.error('Failed to fetch payments:', error);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    }
    fetchPayments();
  }, []);

const getDisplayStatus = (payment) => {
  const storedStatus = String(payment.status || '').toLowerCase();
  if (storedStatus === 'expired') {
    return 'Expired';
  }

  const plan = String(payment.plan_code || '').toUpperCase();

  if (plan === 'FREE_TRIAL') {
    const expiredByCount = Number(payment.free_trial_used_count || 0) >= 5;

    const expiredByDate =
      payment.free_trial_expires_at &&
      new Date(payment.free_trial_expires_at) < new Date();

    return expiredByCount || expiredByDate ? 'Expired' : 'Active';
  }

  if (plan === 'PAY_AS_YOU_GO') {
    const jobsPurchased = Number(payment.payg_jobs_purchased || 0);
    const jobsUsed = Number(payment.payg_jobs_used || 0);

    const totalSlots = jobsPurchased * 5;
    const usedSlots = Number(payment.payg_shortlists_used || 0);

    const expiredByUsage =
      jobsUsed >= jobsPurchased &&
      usedSlots >= totalSlots;

    const expiredByDate =
      payment.payg_expires_at &&
      new Date(payment.payg_expires_at) < new Date();

    return (expiredByUsage || expiredByDate)
      ? 'Expired'
      : 'Active';
  }
if (plan === 'OVERAGE_JOB') {
  const userPlan = String(payment.current_user_plan || '').toLowerCase();

  // Starter overage
  if (userPlan === 'monthly') {
    const jobsUsed = Number(payment.starter_overage_jobs_used || 0);
    const jobLimit = Number(payment.starter_overage_job_limit || 1);

    const shortlistUsed = Number(payment.starter_overage_shortlists_used || 0);
    const shortlistLimit = Number(payment.starter_overage_shortlist_limit || 5);

    const expiredByUsage =
      jobsUsed >= jobLimit &&
      shortlistUsed >= shortlistLimit;

    const expiredByDate =
      payment.starter_overage_expires_at &&
      new Date(payment.starter_overage_expires_at) < new Date();

    return (expiredByUsage || expiredByDate) ? 'Expired' : 'Active';
  }

  // Keep existing behavior for other overage types
  if (userPlan === 'professional' || userPlan === 'annual') {
    const remaining = Number(payment.professional_overage_jobs || 0);

    const expiryOk =
      !payment.professional_overage_expires_at ||
      new Date(payment.professional_overage_expires_at) > new Date();

    return remaining > 0 && expiryOk ? 'Active' : 'Expired';
  }

  return Number(payment.overage_jobs || 0) > 0 ? 'Active' : 'Expired';
}

  if (plan === 'MONTHLY' || plan === 'STARTER') {
    const jobsUsed = Number(payment.starter_jobs_used || 0);
    const jobLimit = Number(payment.starter_job_limit || 10);

    const shortlistUsed = Number(payment.starter_shortlists_used || 0);
    const shortlistLimit = Number(payment.starter_shortlist_limit || 50);

    const expiredByUsage =
      jobsUsed >= jobLimit &&
      shortlistUsed >= shortlistLimit;

    const expiredByDate =
      payment.expires_at &&
      new Date(payment.expires_at) < new Date();

    return (expiredByUsage || expiredByDate) ? 'Expired' : 'Active';
  }

  if (plan === 'PROFESSIONAL' || plan === 'ANNUAL') {
    const jobsUsed = Number(payment.professional_jobs_used || 0);
    const jobLimit = Number(payment.professional_job_limit || 50);

    const shortlistUsed = Number(payment.professional_shortlists_used || 0);
    const shortlistLimit = Number(payment.professional_shortlist_limit || 300);

    const expiredByUsage =
      jobsUsed >= jobLimit &&
      shortlistUsed >= shortlistLimit;

    const expiredByDate =
      payment.expires_at &&
      new Date(payment.expires_at) < new Date();

    return (expiredByUsage || expiredByDate) ? 'Expired' : 'Active';
  }

  if (!payment.expires_at) return 'Active';

  const expiryDate = new Date(payment.expires_at);

  if (isNaN(expiryDate.getTime())) return 'Active';

  return expiryDate > new Date() ? 'Active' : 'Expired';
};
  const handleDownloadReceipt = (payment) => {
    // Basic receipt generation as a simple printable window/content
    const receiptContent = `
      <html>
        <head>
          <title>Payment Receipt - ${payment.razorpay_order_id || payment.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            .receipt-box { border: 1px solid #eee; padding: 30px; border-radius: 10px; max-width: 600px; margin: auto; }
            .header { text-align: center; border-bottom: 2px solid #4338CA; padding-bottom: 20px; margin-bottom: 20px; }
            .header h1 { color: #4338CA; margin: 0; }
            .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
            .label { font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; font-size: 0.9em; color: #777; border-top: 1px solid #eee; padding-top: 20px; }
            .status-captured { color: green; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="receipt-box">
            <div class="header">
              <h1>ExiHire</h1>
              <p>Payment Receipt</p>
            </div>
            <div class="row">
              <span class="label">Date:</span>
              <span>${new Date(payment.created_at).toLocaleString()}</span>
            </div>
            <div class="row">
              <span class="label">Receipt No:</span>
              <span>${payment.id.substring(0, 8).toUpperCase()}</span>
            </div>
            <div class="row">
              <span class="label">Order ID:</span>
              <span>${payment.razorpay_order_id || 'N/A'}</span>
            </div>
            <div class="row">
              <span class="label">Payment ID:</span>
              <span>${payment.payment_id || 'N/A'}</span>
            </div>
            <hr />
            <div class="row">
              <span class="label">Plan:</span>
              <span>${payment.plan_code}</span>
            </div>
            <div class="row">
              <span class="label">Amount:</span>
              <span>₹${payment.amount / 100}</span>
            </div>
            <div class="row">
              <span class="label">Status:</span>
              <span class="status-captured">${payment.status.toUpperCase()}</span>
            </div>
            <div class="footer">
              <p>Thank you for your business!</p>
              <p>This is a computer generated receipt and does not require a physical signature.</p>
              <p>Contact: connect@evolvexinnovations.org</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
  };
  const paginatedPayments = payments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4"
                  sx={{
                    color: "#4F46E5",
                    fontWeight: "bold",
                    mb: 2
                  }}
      >
        Payment History
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 4, borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{window.location.pathname.includes('super-admin') ? 'User / Details' : 'Order ID'}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {paginatedPayments.map((payment) => {
            const displayStatus = getDisplayStatus(payment);

            return (
              <TableRow key={payment.id} hover>
                <TableCell>{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  {payment.first_name ? `${payment.first_name} ${payment.last_name}` : (payment.razorpay_order_id || 'N/A')}
                  {payment.email && <Typography variant="caption" display="block" color="text.secondary">{payment.email}</Typography>}
                </TableCell>
                <TableCell>{planMap[payment.plan_code] || payment.plan_code}</TableCell>
                <TableCell>₹{payment.amount / 100}</TableCell>
                <TableCell>
                  <Chip
                    label={displayStatus}
                    color={displayStatus === 'Active' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    startIcon={<Download />}
                    size="small"
                    variant="outlined"
                    onClick={() => handleDownloadReceipt(payment)}
                    disabled={!['captured', 'expired'].includes(payment.status)}
                  >
                    Receipt
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
            {paginatedPayments.length === 0 && payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No payment records found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={payments.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Container>
  );
}

export default Payment;
